/**
 * Archies — cart drawer history sync.
 *
 * Makes the browser Back button / mobile swipe-back close the cart drawer instead of leaving the
 * page, and reflects the open drawer in the URL (?modals=cart-drawer-dialog, à la David Protein) so
 * the open state is shareable / deep-linkable.
 *
 * Zero core edits — it rides DialogComponent's existing `dialog:open` / `dialog:close` events
 * (assets/dialog.js), which the cart drawer already dispatches on open/close.
 *
 * Flow:
 *   open  → push a history entry (with the ?modals param) so there's something for Back to pop.
 *   Back  → popstate fires; if the drawer is open we close it (history already moved, so we don't
 *           touch it again).
 *   close (button/Esc/outside-click) → unwind our pushed entry so the URL returns to the pre-open
 *           state.
 *   deep-link/reload with ?modals=cart-drawer-dialog → open the drawer, leaving a clean base entry
 *           behind it so Back closes it rather than leaving the page.
 */
(function () {
  'use strict';

  var MODAL = 'cart-drawer-dialog';
  var suppressUnwind = false; // set when a popstate is what closed the drawer

  function drawer() {
    return document.querySelector('cart-drawer-component');
  }

  function dialogOpen() {
    var el = drawer();
    var dialog = el && el.querySelector('dialog');
    return !!(dialog && dialog.open);
  }

  function hasModalParam() {
    try {
      return new URL(window.location.href).searchParams.get('modals') === MODAL;
    } catch (e) {
      return false;
    }
  }

  // Relative URL (pathname + search + hash) with the modals param added or removed.
  function relUrl(withModal) {
    var u = new URL(window.location.href);
    if (withModal) u.searchParams.set('modals', MODAL);
    else u.searchParams.delete('modals');
    return u.pathname + u.search + u.hash;
  }

  function closeDrawer() {
    var el = drawer();
    if (el && typeof el.close === 'function') el.close();
  }

  function openDrawer() {
    var el = drawer();
    if (!el) return;
    if (typeof el.open === 'function') el.open();
    else if (typeof el.showDialog === 'function') el.showDialog();
  }

  function bind(el) {
    if (!el || el.__archiesHistoryBound) return;
    el.__archiesHistoryBound = true;

    // Open → push an entry so Back has something to pop. Guard so repeated opens don't stack.
    el.addEventListener('dialog:open', function () {
      if (!(history.state && history.state.archiesCartDrawer)) {
        history.pushState({ archiesCartDrawer: true }, '', relUrl(true));
      }
    });

    // Close → unwind the entry we pushed (unless a popstate already moved history for us).
    el.addEventListener('dialog:close', function () {
      if (suppressUnwind) {
        suppressUnwind = false;
        return;
      }
      if (history.state && history.state.archiesCartDrawer) history.back();
    });
  }

  // Back/swipe while the drawer is open → close it. popstate already changed history, so the
  // dialog:close handler must NOT pop again.
  window.addEventListener('popstate', function () {
    if (dialogOpen()) {
      suppressUnwind = true;
      closeDrawer();
    }
  });

  function init() {
    var el = drawer();
    if (!el) return;
    bind(el);

    if (hasModalParam() && !dialogOpen()) {
      // Leave a clean base entry, then open — so Back returns to the clean URL and closes the drawer.
      history.replaceState({ archiesCartDrawerBase: true }, '', relUrl(false));
      customElements.whenDefined('cart-drawer-component').then(openDrawer);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
